import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/98 backdrop-blur-lg border-b border-background-200/70 shadow-sm"
          : "bg-background-950/25 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-accent-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <i className="ri-leaf-line text-lg text-background-50"></i>
            </div>
            <span
              className={`text-base md:text-lg font-heading font-semibold tracking-tight transition-colors duration-300 ${
                isScrolled ? "text-foreground-900" : "text-white"
              }`}
            >
              VigorBuy
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-700 hover:text-accent-600"
                  : "text-white/95 hover:text-white"
              }`}
            >
              <i className="ri-home-4-line mr-1 text-xs"></i>
              Home
            </Link>
            <Link
              to="/vigorbuy-spreadsheet-guide"
              className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-700 hover:text-accent-600"
                  : "text-white/95 hover:text-white"
              }`}
            >
              <i className="ri-book-open-line mr-1 text-xs"></i>
              Shopping Guide
            </Link>
            <a
              href="#what-is"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/85 hover:text-white"
              }`}
            >
              What Is
            </a>
            <a
              href="#benefits"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/85 hover:text-white"
              }`}
            >
              Benefits
            </a>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? "text-foreground-600 hover:text-foreground-950"
                    : "text-white/85 hover:text-white"
                }`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-3 w-56 z-50 animate-[fadeIn_0.2s_ease]">
                  <div className="max-h-72 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-5 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-accent-50 transition-colors duration-150 whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="#how-it-works"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/85 hover:text-white"
              }`}
            >
              How It Works
            </a>
            <a
              href="#faq"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/85 hover:text-white"
              }`}
            >
              FAQ
            </a>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? "bg-background-100 text-foreground-800 border border-background-300 hover:bg-background-200"
                  : "bg-white/15 backdrop-blur-sm text-white border border-white/25 hover:bg-white/25"
              }`}
            >
              Vigorbuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-shopping-bag-line ml-1.5 text-xs"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled
                ? "text-foreground-800 hover:bg-background-100"
                : "text-white hover:bg-white/10"
            }`}
          >
            <i
              className={`text-lg ${
                isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-[fadeIn_0.2s_ease] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground-800 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </Link>
            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground-800 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-base"></i>
              Shopping Guide
            </Link>
            <div className="my-2 border-t border-background-200"></div>
            <a
              href="#what-is"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              What Is
            </a>
            <a
              href="#benefits"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Benefits
            </a>
            <div className="px-4 py-3">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-3">
                Browse Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-foreground-600 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors whitespace-nowrap"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-price-tag-3-line text-xs text-accent-400"></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
            <div className="my-2 border-t border-background-200"></div>
            <a
              href="#how-it-works"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              How It Works
            </a>
            <a
              href="#faq"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              FAQ
            </a>
            <div className="flex flex-col gap-2 mt-4 px-4 pb-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 text-center bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                Vigorbuy Sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-3 text-center border-2 border-primary-500 text-primary-500 text-sm font-semibold rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-shopping-bag-line ml-1.5 text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}